import JSZip from "jszip";
import { buildGicsNameHierarchy } from "./gicsSectorUtils";

export const EDITABLE_ENGAGEMENT_EXPORT_COLUMNS = [
  { key: "engagementId", label: "Engagement ID", type: "text", locked: true },
  { key: "engagementName", label: "Engagement Name", type: "text", locked: true },
  { key: "targetRegion", label: "Target Region", type: "dropdown" },
  { key: "targetCountry", label: "Target Country", type: "dropdown" },
  { key: "competency", label: "Competency", type: "text", locked: true },
  { key: "practice", label: "Practice", type: "text", locked: true },
  { key: "gicsSector", label: "Sector", type: "dropdown" },
  { key: "industryGroup", label: "Industry Group", type: "dropdown" },
  { key: "industry", label: "Industry", type: "dropdown" },
  { key: "subindustry", label: "Sub Industry", type: "dropdown" },
  { key: "revenueRange", label: "Reported Revenue Range", type: "dropdown" },
  { key: "freeTextTags", label: "Additional Tags", type: "text" },
  { key: "uploadStatus", label: "Upload Status", type: "text", locked: true },
];

// Kept out of EDITABLE_ENGAGEMENT_EXPORT_COLUMNS because that list also defines the import
// schema, and Source is a read-only, engagement-admin-only export column.
const SOURCE_EXPORT_COLUMN = { key: "dataSource", label: "Source", type: "text", locked: true };

export const getEditableEngagementExportColumns = ({ includeSource = false } = {}) => {
  if (!includeSource) return EDITABLE_ENGAGEMENT_EXPORT_COLUMNS;
  const insertIndex =
    EDITABLE_ENGAGEMENT_EXPORT_COLUMNS.findIndex((column) => column.key === "engagementName") + 1;
  return [
    ...EDITABLE_ENGAGEMENT_EXPORT_COLUMNS.slice(0, insertIndex),
    SOURCE_EXPORT_COLUMN,
    ...EDITABLE_ENGAGEMENT_EXPORT_COLUMNS.slice(insertIndex),
  ];
};

const XLSX_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const DEFAULT_SELECT_OPTION = "Select";
const EDITABLE_ENGAGEMENT_IMPORT_REQUIRED_KEYS = [
  "targetRegion",
  "targetCountry",
  "gicsSector",
  "industryGroup",
  "industry",
  "subindustry",
  "revenueRange",
];

// Parent/child chains for dependent dropdowns; order matters so resets cascade down the hierarchy.
const DEPENDENT_DROPDOWN_CONFIG = [
  { key: "targetCountry", parentKey: "targetRegion", mappingKey: "targetCountryByRegion" },
  { key: "industryGroup", parentKey: "gicsSector", mappingKey: "industryGroupBySector" },
  { key: "industry", parentKey: "industryGroup", mappingKey: "industryByIndustryGroup" },
  { key: "subindustry", parentKey: "industry", mappingKey: "subIndustryByIndustry" },
];
const LOCKED_EXPORT_KEYS = new Set(
  [...EDITABLE_ENGAGEMENT_EXPORT_COLUMNS, SOURCE_EXPORT_COLUMN]
    .filter((column) => column.locked)
    .map((column) => column.key)
);
const CONDITIONAL_LOCKED_EXPORT_KEYS = new Set(["targetRegion", "targetCountry"]);
const UNLOCKED_CELL_STYLE_INDEX = 0;
const LOCKED_CELL_STYLE_INDEX = 1;

const uniqueSorted = (values = []) =>
  Array.from(new Set(values.filter(Boolean).map((item) => String(item).trim()))).sort(
    (left, right) => left.localeCompare(right)
  );

const normalizeDefinedNameToken = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  const fallback = normalized || "value";
  return /^[A-Za-z_]/.test(fallback) ? fallback : `n_${fallback}`;
};

const makeUniqueDefinedName = (baseName, usedNames) => {
  const safeBase = normalizeDefinedNameToken(baseName).slice(0, 240);
  let candidate = safeBase;
  let index = 2;
  while (usedNames.has(candidate)) {
    candidate = `${safeBase}_${index}`.slice(0, 255);
    index += 1;
  }
  usedNames.add(candidate);
  return candidate;
};

const ensureDropdownValues = (values = []) => {
  const normalized = uniqueSorted(values).filter(
    (item) => item.toLowerCase() !== DEFAULT_SELECT_OPTION.toLowerCase()
  );
  return [DEFAULT_SELECT_OPTION, ...normalized];
};

const addGroupedMapValue = (groupedMap, parentValue, childValue) => {
  const parent = String(parentValue ?? "").trim();
  const child = String(childValue ?? "").trim();
  if (!parent || !child) return;
  if (!groupedMap[parent]) groupedMap[parent] = new Set();
  groupedMap[parent].add(child);
};

const sortGroupedMapValues = (groupedMap = {}) =>
  Object.keys(groupedMap)
    .sort((left, right) => left.localeCompare(right))
    .reduce((accumulator, parent) => {
      accumulator[parent] = Array.from(groupedMap[parent]).sort((left, right) =>
        left.localeCompare(right)
      );
      return accumulator;
    }, {});

const xmlEscape = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const getTimestamp = () => {
  const date = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(
    date.getDate()
  )}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

const columnLetter = (index) => {
  let dividend = index + 1;
  let columnName = "";
  while (dividend > 0) {
    const modulo = (dividend - 1) % 26;
    columnName = String.fromCharCode(65 + modulo) + columnName;
    dividend = Math.floor((dividend - modulo) / 26);
  }
  return columnName;
};

const normalizeHeaderToken = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normalizeComparableValue = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normalizeTransactionTypeToken = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normalizeTransactionTypeCategory = (value) => {
  const normalized = normalizeTransactionTypeToken(value);
  if (!normalized || normalized === "tbd" || normalized === "to be determined") return "";
  if (normalized.includes("buy")) return "buy-side";
  if (normalized.includes("sell")) return "sell-side";
  if (normalized.includes("not") && normalized.includes("transaction")) return "not a transaction";
  if (normalized === "other") return "not a transaction";
  return normalized;
};

const shouldLockTargetRegionAndCountry = (transactionType) => {
  const normalizedCategory = normalizeTransactionTypeCategory(transactionType);
  return normalizedCategory === "sell-side" || normalizedCategory === "not a transaction";
};

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveExportUploadStatus = (engagement = {}) => {
  const pendingFiles = toFiniteNumber(engagement?.totalPendingFile);
  if (pendingFiles !== null && pendingFiles > 0) return "Provide password";

  const uploadedFiles = toFiniteNumber(engagement?.noOfUploadedFiles);
  if (uploadedFiles !== null && uploadedFiles === 0) return "No files";

  return String(
    engagement?.engagementTrackingStatus ??
      engagement?.uploadStatus ??
      engagement?.uploaD_STATUS ??
      ""
  ).trim();
};

const isBlankOrSelect = (value) => {
  const normalized = normalizeComparableValue(value);
  return !normalized || normalized === DEFAULT_SELECT_OPTION.toLowerCase();
};

const buildNormalizedDependencyLookup = (mapping = {}) =>
  Object.entries(mapping || {}).reduce((accumulator, [parentValue, childValues]) => {
    const parentKey = normalizeComparableValue(parentValue);
    if (!parentKey) return accumulator;
    accumulator[parentKey] = new Set(
      (childValues || []).map((childValue) => normalizeComparableValue(childValue)).filter(Boolean)
    );
    return accumulator;
  }, {});

const shouldDefaultDependentValue = ({
  parentValue,
  childValue,
  dependencyLookup = {},
  hasDependencyMappings = false,
}) => {
  if (isBlankOrSelect(parentValue) || isBlankOrSelect(childValue)) return true;
  if (!hasDependencyMappings) return false;
  const allowedChildValues = dependencyLookup[normalizeComparableValue(parentValue)];
  if (!allowedChildValues || allowedChildValues.size === 0) return true;
  return !allowedChildValues.has(normalizeComparableValue(childValue));
};

const parseCsvLine = (line = "") => {
  const values = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      values.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current);
  return values;
};

const getColumnIndexFromCellRef = (cellReference = "") => {
  const letters = String(cellReference).match(/[A-Za-z]+/)?.[0] || "";
  let index = 0;
  for (let i = 0; i < letters.length; i += 1) {
    index = index * 26 + (letters.charCodeAt(i) - 64);
  }
  return Math.max(0, index - 1);
};

const getSharedStrings = (sharedStringsXml = "") => {
  if (!sharedStringsXml) return [];
  const parser = new DOMParser();
  const sharedDoc = parser.parseFromString(sharedStringsXml, "application/xml");
  return Array.from(sharedDoc.getElementsByTagName("si")).map((item) =>
    Array.from(item.getElementsByTagName("t"))
      .map((node) => node.textContent || "")
      .join("")
  );
};

const getCellTextValue = (cellNode, sharedStrings = []) => {
  const type = cellNode?.getAttribute("t");
  if (type === "inlineStr") {
    return Array.from(cellNode.getElementsByTagName("t"))
      .map((node) => node.textContent || "")
      .join("");
  }
  const rawValue = cellNode.getElementsByTagName("v")[0]?.textContent ?? "";
  if (type === "s") {
    const index = Number(rawValue);
    return Number.isFinite(index) ? sharedStrings[index] || "" : "";
  }
  return rawValue;
};

const mapRawRowsToSchemaRows = ({ headers = [], rows = [] }) => {
  const normalizedHeaderMap = new Map(
    (headers || []).map((header) => [normalizeHeaderToken(header), header])
  );
  return (rows || []).map((row, index) =>
    EDITABLE_ENGAGEMENT_EXPORT_COLUMNS.reduce(
      (accumulator, column) => {
        const matchedHeader = normalizedHeaderMap.get(normalizeHeaderToken(column.label));
        accumulator[column.key] = String(row?.[matchedHeader] ?? "").trim();
        return accumulator;
      },
      { __rowNumber: row?.__rowNumber || index + 2 }
    )
  );
};

const buildInlineStringCell = (reference, value, styleId = null) => {
  const stringValue = String(value ?? "");
  const spaceAttribute =
    stringValue.startsWith(" ") || stringValue.endsWith(" ")
      ? ' xml:space="preserve"'
      : "";
  const styleAttribute = Number.isInteger(styleId) && styleId >= 0 ? ` s="${styleId}"` : "";
  return `<c r="${reference}"${styleAttribute} t="inlineStr"><is><t${spaceAttribute}>${xmlEscape(
    stringValue
  )}</t></is></c>`;
};

const buildSheetDataXml = (headers = [], records = [], columns = EDITABLE_ENGAGEMENT_EXPORT_COLUMNS) => {
  const rows = [];
  const headerCells = headers
    .map((header, index) => buildInlineStringCell(`${columnLetter(index)}1`, header))
    .join("");
  rows.push(`<row r="1">${headerCells}</row>`);

  records.forEach((record, rowIndex) => {
    const rowNumber = rowIndex + 2;
    const conditionalLockedKeys = new Set(record?.__lockedKeys || []);
    const rowCells = headers
      .map((_, colIndex) => {
        const key = columns[colIndex]?.key;
        const styleId = LOCKED_EXPORT_KEYS.has(key) || conditionalLockedKeys.has(key)
          ? LOCKED_CELL_STYLE_INDEX
          : UNLOCKED_CELL_STYLE_INDEX;
        return buildInlineStringCell(
          `${columnLetter(colIndex)}${rowNumber}`,
          record?.[key] ?? "",
          styleId
        );
      })
      .join("");
    rows.push(`<row r="${rowNumber}">${rowCells}</row>`);
  });

  return `<sheetData>${rows.join("")}</sheetData>`;
};

export const buildEditableEngagementExportRows = (
  engagements = [],
  userData = {},
  columns = EDITABLE_ENGAGEMENT_EXPORT_COLUMNS
) => {
  const dropdownSources = buildDropdownSources(userData);
  const dependencyConfig = DEPENDENT_DROPDOWN_CONFIG.reduce((accumulator, item) => {
    accumulator[item.key] = {
      parentKey: item.parentKey,
      hasMappings: Object.keys(dropdownSources[item.mappingKey] || {}).length > 0,
      lookup: buildNormalizedDependencyLookup(dropdownSources[item.mappingKey]),
    };
    return accumulator;
  }, {});

  return (engagements || []).map((engagement) => {
    const exportRow = columns.reduce((accumulator, column) => {
      const currentValue =
        column.key === "uploadStatus"
          ? resolveExportUploadStatus(engagement)
          : engagement?.[column.key] ?? "";
      const normalizedValue = String(currentValue).trim();
      accumulator[column.key] =
        column.type === "dropdown" && !normalizedValue ? DEFAULT_SELECT_OPTION : currentValue;
      return accumulator;
    }, {});

    DEPENDENT_DROPDOWN_CONFIG.map((item) => item.key).forEach((dependentKey) => {
      const config = dependencyConfig[dependentKey];
      if (!config) return;
      if (
        shouldDefaultDependentValue({
          parentValue: exportRow[config.parentKey],
          childValue: exportRow[dependentKey],
          dependencyLookup: config.lookup,
          hasDependencyMappings: config.hasMappings,
        })
      ) {
        exportRow[dependentKey] = DEFAULT_SELECT_OPTION;
      }
    });

    if (shouldLockTargetRegionAndCountry(engagement?.transactionType)) {
      Object.defineProperty(exportRow, "__lockedKeys", {
        value: Array.from(CONDITIONAL_LOCKED_EXPORT_KEYS),
        enumerable: false,
        configurable: false,
        writable: false,
      });
    }

    return exportRow;
  });
};

export const buildDropdownSources = (userData = {}) => {
  const regions = uniqueSorted(
    (userData?.areaRegionCountryMappings || []).map((item) => item?.regionName)
  );
  const countries = uniqueSorted(
    (userData?.areaRegionCountryMappings || []).map((item) => item?.countryName)
  );
  const targetCountryByRegionGrouped = {};
  (userData?.areaRegionCountryMappings || []).forEach((item) => {
    addGroupedMapValue(targetCountryByRegionGrouped, item?.regionName, item?.countryName);
  });

  const competencyNameById = new Map(
    (userData?.competencyList || []).map((item) => [
      String(item?.competencyID ?? ""),
      item?.competencyName,
    ])
  );
  const practiceByCompetencyGrouped = {};
  (userData?.practiceList || []).forEach((item) => {
    const competencyName =
      competencyNameById.get(String(item?.competencyID ?? "")) || item?.competencyName;
    addGroupedMapValue(practiceByCompetencyGrouped, competencyName, item?.practiceName);
  });

  const gicsHierarchy = buildGicsNameHierarchy(userData?.gicsSectorMappings);

  return {
    targetRegion: regions,
    targetCountry: countries,
    competency: uniqueSorted(
      (userData?.competencyList || []).map((item) => item?.competencyName)
    ),
    practice: uniqueSorted((userData?.practiceList || []).map((item) => item?.practiceName)),
    gicsSector: gicsHierarchy.sectors,
    industryGroup: gicsHierarchy.industryGroups,
    industry: gicsHierarchy.industries,
    subindustry: gicsHierarchy.subIndustries,
    revenueRange: uniqueSorted(
      (userData?.revenueRanges || [])
        .map((item) => item?.range)
        .filter((range) => String(range || "").toLowerCase() !== "others")
    ),
    targetCountryByRegion: sortGroupedMapValues(targetCountryByRegionGrouped),
    practiceByCompetency: sortGroupedMapValues(practiceByCompetencyGrouped),
    industryGroupBySector: gicsHierarchy.industryGroupsBySector,
    industryByIndustryGroup: gicsHierarchy.industriesByIndustryGroup,
    subIndustryByIndustry: gicsHierarchy.subIndustriesByIndustry,
  };
};

const buildDropdownWorkbookData = (dropdownSources = {}) => {
  const sheetColumns = [];
  const namedRanges = [];
  const usedDefinedNames = new Set();
  const validationConfig = {
    staticListNames: {},
    dependentListConfigs: {},
    emptyListName: "emptyList",
  };

  const addNamedColumn = ({ baseName, header, values }) => {
    const definedName = makeUniqueDefinedName(baseName, usedDefinedNames);
    const normalizedValues = ensureDropdownValues(values);
    const columnIndex = sheetColumns.length;
    const columnRef = columnLetter(columnIndex);
    sheetColumns.push({ header, values: normalizedValues });
    namedRanges.push({
      name: definedName,
      ref: `'Dropdowns'!$${columnRef}$2:$${columnRef}$${normalizedValues.length + 1}`,
    });
    return definedName;
  };

  const addLookupRange = ({ baseName, headerPrefix, mapping = {}, childBasePrefix }) => {
    const pairs = [];
    Object.entries(mapping || {})
      .sort(([left], [right]) => String(left).localeCompare(String(right)))
      .forEach(([parentValue, childValues]) => {
        const childDefinedName = addNamedColumn({
          baseName: `${childBasePrefix}_${parentValue}`,
          header: `${headerPrefix} | ${parentValue}`,
          values: childValues,
        });
        pairs.push({ parentValue, childDefinedName });
      });

    const lookupParents = pairs.map((item) => item.parentValue);
    const lookupRangeNames = pairs.map((item) => item.childDefinedName);
    if (!lookupParents.length) {
      lookupParents.push("");
      lookupRangeNames.push(validationConfig.emptyListName);
    }

    const rowCount = Math.max(lookupParents.length, lookupRangeNames.length, 1);
    const firstColumnIndex = sheetColumns.length;
    sheetColumns.push({ header: `${headerPrefix} Parent`, values: lookupParents });
    sheetColumns.push({ header: `${headerPrefix} Range`, values: lookupRangeNames });

    const lookupDefinedName = makeUniqueDefinedName(baseName, usedDefinedNames);
    const startColumn = columnLetter(firstColumnIndex);
    const endColumn = columnLetter(firstColumnIndex + 1);
    namedRanges.push({
      name: lookupDefinedName,
      ref: `'Dropdowns'!$${startColumn}$2:$${endColumn}$${rowCount + 1}`,
    });
    return lookupDefinedName;
  };

  const emptyListName = addNamedColumn({
    baseName: "emptyList",
    header: "Empty List",
    values: [DEFAULT_SELECT_OPTION],
  });
  validationConfig.emptyListName = emptyListName;

  validationConfig.staticListNames.targetRegion = addNamedColumn({
    baseName: "targetRegionOptions",
    header: "Target Region",
    values: dropdownSources.targetRegion,
  });
  validationConfig.staticListNames.targetCountry = addNamedColumn({
    baseName: "targetCountryOptions",
    header: "Target Country",
    values: dropdownSources.targetCountry,
  });
  validationConfig.staticListNames.gicsSector = addNamedColumn({
    baseName: "sectorOptions",
    header: "Sector",
    values: dropdownSources.gicsSector,
  });
  validationConfig.staticListNames.industryGroup = addNamedColumn({
    baseName: "industryGroupOptions",
    header: "Industry Group",
    values: dropdownSources.industryGroup,
  });
  validationConfig.staticListNames.industry = addNamedColumn({
    baseName: "industryOptions",
    header: "Industry",
    values: dropdownSources.industry,
  });
  validationConfig.staticListNames.subindustry = addNamedColumn({
    baseName: "subIndustryOptions",
    header: "Sub Industry",
    values: dropdownSources.subindustry,
  });
  validationConfig.staticListNames.revenueRange = addNamedColumn({
    baseName: "revenueRangeOptions",
    header: "Revenue Range",
    values: dropdownSources.revenueRange,
  });

  validationConfig.dependentListConfigs.targetCountry = {
    parentKey: "targetRegion",
    lookupRangeName: addLookupRange({
      baseName: "targetCountryLookup",
      headerPrefix: "Target Country Lookup",
      mapping: dropdownSources.targetCountryByRegion,
      childBasePrefix: "targetCountry",
    }),
  };
  validationConfig.dependentListConfigs.industryGroup = {
    parentKey: "gicsSector",
    lookupRangeName: addLookupRange({
      baseName: "industryGroupLookup",
      headerPrefix: "Industry Group Lookup",
      mapping: dropdownSources.industryGroupBySector,
      childBasePrefix: "industryGroup",
    }),
  };
  validationConfig.dependentListConfigs.industry = {
    parentKey: "industryGroup",
    lookupRangeName: addLookupRange({
      baseName: "industryLookup",
      headerPrefix: "Industry Lookup",
      mapping: dropdownSources.industryByIndustryGroup,
      childBasePrefix: "industry",
    }),
  };
  validationConfig.dependentListConfigs.subindustry = {
    parentKey: "industry",
    lookupRangeName: addLookupRange({
      baseName: "subIndustryLookup",
      headerPrefix: "Sub Industry Lookup",
      mapping: dropdownSources.subIndustryByIndustry,
      childBasePrefix: "subIndustry",
    }),
  };

  return { sheetColumns, namedRanges, validationConfig };
};

const buildDropdownSheetXml = (dropdownSheetColumns = []) => {
  const safeColumns = dropdownSheetColumns.length
    ? dropdownSheetColumns
    : [{ header: "Empty List", values: [""] }];
  const maxOptions = Math.max(
    1,
    ...safeColumns.map((column) => (column.values || []).length)
  );
  const rows = [];

  const headerCells = safeColumns
    .map((column, index) => buildInlineStringCell(`${columnLetter(index)}1`, column.header))
    .join("");
  rows.push(`<row r="1">${headerCells}</row>`);

  for (let optionIndex = 0; optionIndex < maxOptions; optionIndex += 1) {
    const rowNumber = optionIndex + 2;
    const rowCells = safeColumns
      .map((column, colIndex) =>
        buildInlineStringCell(
          `${columnLetter(colIndex)}${rowNumber}`,
          column.values?.[optionIndex] ?? ""
        )
      )
      .join("");
    rows.push(`<row r="${rowNumber}">${rowCells}</row>`);
  }

  const lastColumn = columnLetter(Math.max(0, safeColumns.length - 1));
  const dimension = `A1:${lastColumn}${Math.max(2, maxOptions + 1)}`;

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="${dimension}"/>
  <sheetViews><sheetView workbookViewId="0"/></sheetViews>
  <sheetFormatPr defaultRowHeight="15"/>
  ${`<sheetData>${rows.join("")}</sheetData>`}
  <pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>
</worksheet>`;
};

const buildDataValidationsXml = (
  headers = [],
  dropdownColumns = [],
  validationConfig = {},
  columns = EDITABLE_ENGAGEMENT_EXPORT_COLUMNS
) => {
  const columnIndexByKey = columns.reduce((accumulator, column, index) => {
    accumulator[column.key] = index;
    return accumulator;
  }, {});
  const validations = dropdownColumns
    .map((column) => {
      const mainColumnIndex = headers.findIndex((header) => header === column.label);
      if (mainColumnIndex < 0) return "";
      const mainColumn = columnLetter(mainColumnIndex);
      const dependentConfig = validationConfig.dependentListConfigs?.[column.key];
      const fallbackListName =
        validationConfig.staticListNames?.[column.key] || validationConfig.emptyListName;
      let formula = fallbackListName || validationConfig.emptyListName || "emptyList";

      if (dependentConfig?.parentKey && dependentConfig?.lookupRangeName) {
        const parentColumnIndex = columnIndexByKey[dependentConfig.parentKey];
        if (parentColumnIndex !== undefined) {
          const parentColumn = columnLetter(parentColumnIndex);
          const emptyListName = validationConfig.emptyListName || "emptyList";
          const fallbackName = fallbackListName || emptyListName;
          formula = `IF(OR($${parentColumn}2="",$${parentColumn}2="${DEFAULT_SELECT_OPTION}"),"${emptyListName}",INDIRECT(IFERROR(VLOOKUP($${parentColumn}2,${dependentConfig.lookupRangeName},2,FALSE),"${fallbackName}")))`;
        }
      }

      return `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="1" sqref="${mainColumn}2:${mainColumn}1048576"><formula1>${xmlEscape(
        formula
      )}</formula1></dataValidation>`;
    })
    .filter(Boolean);

  const additionalTagsColumnIndex = columns.findIndex(
    (column) => column.key === "freeTextTags"
  );
  if (additionalTagsColumnIndex >= 0) {
    const additionalTagsColumn = columnLetter(additionalTagsColumnIndex);
    const tagsCountFormula = `LEN($${additionalTagsColumn}2)-LEN(SUBSTITUTE($${additionalTagsColumn}2,",",""))+1`;
    const tagSliceFormula = `TRIM(MID(SUBSTITUTE($${additionalTagsColumn}2,",",REPT(" ",99)),(ROW(INDIRECT("1:"&${tagsCountFormula}))-1)*99+1,99))`;
    const additionalTagsFormula = `IF($${additionalTagsColumn}2="",TRUE,AND(LEFT($${additionalTagsColumn}2,1)<>",",RIGHT($${additionalTagsColumn}2,1)<>",",ISERROR(SEARCH(",,",$${additionalTagsColumn}2)),SUMPRODUCT(--ISNUMBER(SEARCH(MID($${additionalTagsColumn}2,ROW(INDIRECT("1:"&LEN($${additionalTagsColumn}2))),1),"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz, ")))=LEN($${additionalTagsColumn}2),SUMPRODUCT(--(LEN(${tagSliceFormula})>0))=${tagsCountFormula},${tagsCountFormula}<=15))`;
    validations.push(
      `<dataValidation type="custom" allowBlank="1" showInputMessage="1" showErrorMessage="1" errorTitle="Invalid Additional Tags" error="Use comma-separated alphabetic tags (spaces allowed), up to 15 total tags." promptTitle="Additional Tags" prompt="Enter comma-separated alphabetic tags with optional spaces (up to 15 total)." sqref="${additionalTagsColumn}2:${additionalTagsColumn}1048576"><formula1>${xmlEscape(
        additionalTagsFormula
      )}</formula1></dataValidation>`
    );
  }

  if (!validations.length) return "";
  return `<dataValidations count="${validations.length}">${validations.join("")}</dataValidations>`;
};

const buildMainSheetXml = (
  records = [],
  dropdownColumns = [],
  validationConfig = {},
  columns = EDITABLE_ENGAGEMENT_EXPORT_COLUMNS
) => {
  const headers = columns.map((column) => column.label);
  const lastColumn = columnLetter(headers.length - 1);
  const lastRow = Math.max(2, records.length + 1);
  const sheetDataXml = buildSheetDataXml(headers, records, columns);
  const validationsXml = buildDataValidationsXml(headers, dropdownColumns, validationConfig, columns);

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="A1:${lastColumn}${lastRow}"/>
  <sheetViews><sheetView workbookViewId="0"/></sheetViews>
  <sheetFormatPr defaultRowHeight="15"/>
  ${sheetDataXml}
  <sheetProtection sheet="1" objects="1" scenarios="1" formatCells="0" formatColumns="0" formatRows="0" insertColumns="0" insertRows="0" insertHyperlinks="0" deleteColumns="0" deleteRows="0" sort="0" autoFilter="0" pivotTables="0" selectLockedCells="0" selectUnlockedCells="0"/>
  ${validationsXml}
  <pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>
</worksheet>`;
};

const buildWorkbookXml = (namedRanges = []) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="Engagements" sheetId="1" r:id="rId1"/>
    <sheet name="Dropdowns" sheetId="2" state="hidden" r:id="rId2"/>
  </sheets>
  ${
    namedRanges.length
      ? `<definedNames>${namedRanges
          .map(
            ({ name, ref }) =>
              `<definedName name="${xmlEscape(name)}">${xmlEscape(ref)}</definedName>`
          )
          .join("")}</definedNames>`
      : ""
  }
</workbook>`;

const buildWorkbookRelsXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet2.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

const buildRootRelsXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const buildContentTypesXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/worksheets/sheet2.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

const buildStylesXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts>
  <fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="2">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyProtection="1"><protection locked="0"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

export const buildCsvContent = (records = [], columns = EDITABLE_ENGAGEMENT_EXPORT_COLUMNS) => {
  const headers = columns.map((column) => column.label);
  const escapeCsv = (value) => {
    const normalized = String(value ?? "");
    if (
      normalized.includes(",") ||
      normalized.includes('"') ||
      normalized.includes("\n") ||
      normalized.includes("\r")
    ) {
      return `"${normalized.replace(/"/g, '""')}"`;
    }
    return normalized;
  };

  const rows = [
    headers.join(","),
    ...records.map((record) =>
      columns.map((column) =>
        escapeCsv(record?.[column.key] ?? "")
      ).join(",")
    ),
  ];
  return rows.join("\r\n");
};

export const parseEditableEngagementImportCsvContent = (csvContent = "") => {
  const lines = String(csvContent || "")
    .split(/\r\n|\n|\r/)
    .filter((line) => line.trim().length > 0);
  if (!lines.length) {
    return { headers: [], rows: [] };
  }
  const headers = parseCsvLine(lines[0]).map((header) => String(header ?? "").trim());
  const rows = lines
    .slice(1)
    .map((line, rowIndex) => {
      const values = parseCsvLine(line);
      const row = headers.reduce((accumulator, header, colIndex) => {
        accumulator[header] = String(values[colIndex] ?? "").trim();
        return accumulator;
      }, {});
      row.__rowNumber = rowIndex + 2;
      return row;
    })
    .filter((row) =>
      Object.keys(row)
        .filter((key) => key !== "__rowNumber")
        .some((key) => String(row[key] || "").trim() !== "")
    );

  return { headers, rows };
};

export const parseEditableEngagementImportXlsxBuffer = async (xlsxBuffer) => {
  const zip = await JSZip.loadAsync(xlsxBuffer);
  const workbookXml = await zip.file("xl/workbook.xml")?.async("string");
  const workbookRelsXml = await zip.file("xl/_rels/workbook.xml.rels")?.async("string");
  if (!workbookXml || !workbookRelsXml) {
    throw new Error("Invalid workbook structure.");
  }

  const parser = new DOMParser();
  const workbookDoc = parser.parseFromString(workbookXml, "application/xml");
  const relsDoc = parser.parseFromString(workbookRelsXml, "application/xml");

  const sheetNodes = Array.from(workbookDoc.getElementsByTagName("sheet"));
  const engagementsSheet =
    sheetNodes.find(
      (sheet) => normalizeHeaderToken(sheet.getAttribute("name")) === "engagements"
    ) || sheetNodes[0];

  const relationId = engagementsSheet?.getAttribute("r:id");
  if (!relationId) {
    throw new Error("Engagement sheet relation not found.");
  }

  const relationNode = Array.from(relsDoc.getElementsByTagName("Relationship")).find(
    (relationship) => relationship.getAttribute("Id") === relationId
  );
  if (!relationNode) {
    throw new Error("Engagement sheet mapping not found.");
  }

  const targetPath = relationNode.getAttribute("Target") || "";
  const normalizedTarget = targetPath.startsWith("/")
    ? targetPath.slice(1)
    : `xl/${targetPath.replace(/^\/+/, "")}`;
  const sheetPath = normalizedTarget.startsWith("xl/")
    ? normalizedTarget
    : `xl/${normalizedTarget}`;
  const sheetXml =
    (await zip.file(sheetPath)?.async("string")) ||
    (await zip.file("xl/worksheets/sheet1.xml")?.async("string"));
  if (!sheetXml) {
    throw new Error("Engagement sheet not found in workbook.");
  }

  const sharedStringsXml = await zip.file("xl/sharedStrings.xml")?.async("string");
  const sharedStrings = getSharedStrings(sharedStringsXml);
  const sheetDoc = parser.parseFromString(sheetXml, "application/xml");
  const rowNodes = Array.from(sheetDoc.getElementsByTagName("row"));
  if (!rowNodes.length) {
    return { headers: [], rows: [] };
  }

  const parsedRows = rowNodes.map((rowNode, rowIndex) => {
    const cells = Array.from(rowNode.getElementsByTagName("c"));
    const rowValues = {};
    cells.forEach((cellNode, cellIndex) => {
      const reference = cellNode.getAttribute("r");
      const colIndex = reference
        ? getColumnIndexFromCellRef(reference)
        : cellIndex;
      rowValues[colIndex] = String(getCellTextValue(cellNode, sharedStrings) ?? "").trim();
    });
    return { __rowNumber: rowIndex + 1, values: rowValues };
  });

  const headerRow = parsedRows[0]?.values || {};
  const maxHeaderCol = Math.max(0, ...Object.keys(headerRow).map((key) => Number(key)));
  const headers = Array.from({ length: maxHeaderCol + 1 }, (_, index) =>
    String(headerRow[index] ?? "").trim()
  );

  const rows = parsedRows
    .slice(1)
    .map((row) => {
      const rowValue = headers.reduce((accumulator, header, colIndex) => {
        if (!header) return accumulator;
        accumulator[header] = String(row.values[colIndex] ?? "").trim();
        return accumulator;
      }, {});
      rowValue.__rowNumber = row.__rowNumber;
      return rowValue;
    })
    .filter((row) =>
      Object.keys(row)
        .filter((key) => key !== "__rowNumber")
        .some((key) => String(row[key] || "").trim() !== "")
    );

  return { headers, rows };
};

export const parseEditableEngagementImportFile = async (file) => {
  const fileName = String(file?.name || "").toLowerCase();
  if (!fileName) throw new Error("No import file selected.");

  if (fileName.endsWith(".csv")) {
    const csvContent = await file.text();
    const parsed = parseEditableEngagementImportCsvContent(csvContent);
    return { ...parsed, rows: mapRawRowsToSchemaRows(parsed) };
  }

  if (fileName.endsWith(".xlsx")) {
    const buffer = await file.arrayBuffer();
    const parsed = await parseEditableEngagementImportXlsxBuffer(buffer);
    return { ...parsed, rows: mapRawRowsToSchemaRows(parsed) };
  }

  throw new Error("Unsupported file format. Please upload .xlsx or .csv.");
};

export const validateEditableEngagementImportRows = ({
  rows = [],
  headers = [],
  fileData = [],
  userData = {},
}) => {
  const missingColumns = [];
  const normalizedHeaders = new Set((headers || []).map(normalizeHeaderToken));
  const requiredColumnLabels = [
    "Engagement ID",
    ...EDITABLE_ENGAGEMENT_IMPORT_REQUIRED_KEYS.map(
      (key) => EDITABLE_ENGAGEMENT_EXPORT_COLUMNS.find((column) => column.key === key)?.label
    ),
  ].filter(Boolean);

  requiredColumnLabels.forEach((label) => {
    if (!normalizedHeaders.has(normalizeHeaderToken(label))) {
      missingColumns.push(label);
    }
  });

  if (missingColumns.length > 0) {
    return {
      missingColumns,
      summary: {
        totalRecords: 0,
        validRecords: 0,
        warningRecords: 0,
        errorRecords: 0,
      },
      validRows: [],
      failedRows: [],
    };
  }

  const dropdownSources = buildDropdownSources(userData);
  const lookupSets = {
    targetRegion: new Set((dropdownSources.targetRegion || []).map(normalizeComparableValue)),
    targetCountry: new Set((dropdownSources.targetCountry || []).map(normalizeComparableValue)),
    gicsSector: new Set((dropdownSources.gicsSector || []).map(normalizeComparableValue)),
    industryGroup: new Set((dropdownSources.industryGroup || []).map(normalizeComparableValue)),
    industry: new Set((dropdownSources.industry || []).map(normalizeComparableValue)),
    subindustry: new Set((dropdownSources.subindustry || []).map(normalizeComparableValue)),
    revenueRange: new Set((dropdownSources.revenueRange || []).map(normalizeComparableValue)),
  };

  const dependencyLookup = DEPENDENT_DROPDOWN_CONFIG.reduce((accumulator, item) => {
    accumulator[item.key] = buildNormalizedDependencyLookup(dropdownSources[item.mappingKey]);
    return accumulator;
  }, {});
  const getColumnLabel = (key) =>
    EDITABLE_ENGAGEMENT_EXPORT_COLUMNS.find((column) => column.key === key)?.label || key;

  const engagementMap = new Map(
    (fileData || []).map((item) => [normalizeComparableValue(item?.engagementId), item])
  );

  const failedRows = [];
  const validRows = [];

  (rows || []).forEach((row) => {
    const rowErrors = [];
    const rowWarnings = [];
    const rowNumber = row?.__rowNumber || 0;
    const engagementId = String(row?.engagementId || "").trim();
    const engagementKey = normalizeComparableValue(engagementId);
    const existingRow = engagementMap.get(engagementKey);

    if (!engagementId) {
      rowErrors.push("Engagement ID is required.");
    } else if (!existingRow) {
      rowErrors.push(`Engagement ID "${engagementId}" was not found in current portal data.`);
    }

    const fieldValidationState = {};
    EDITABLE_ENGAGEMENT_IMPORT_REQUIRED_KEYS.forEach((field) => {
      const value = row?.[field];
      if (isBlankOrSelect(value)) {
        const label = EDITABLE_ENGAGEMENT_EXPORT_COLUMNS.find(
          (column) => column.key === field
        )?.label;
        rowErrors.push(`${label || field} is required.`);
        fieldValidationState[field] = {
          isRequiredPassed: false,
          isLookupValid: false,
        };
        return;
      }
      const normalizedValue = normalizeComparableValue(value);
      const isLookupValid = Boolean(lookupSets[field]?.has(normalizedValue));
      if (!isLookupValid) {
        const label = EDITABLE_ENGAGEMENT_EXPORT_COLUMNS.find(
          (column) => column.key === field
        )?.label;
        rowErrors.push(`${label || field} value "${value}" is not valid.`);
      }
      fieldValidationState[field] = {
        isRequiredPassed: true,
        isLookupValid,
      };
    });

    DEPENDENT_DROPDOWN_CONFIG.forEach(({ key, parentKey }) => {
      const canValidateMapping =
        fieldValidationState[parentKey]?.isRequiredPassed &&
        fieldValidationState[parentKey]?.isLookupValid &&
        fieldValidationState[key]?.isRequiredPassed &&
        fieldValidationState[key]?.isLookupValid;
      if (!canValidateMapping) return;
      const parentValue = String(row?.[parentKey] || "").trim();
      const childValue = String(row?.[key] || "").trim();
      const allowedValues =
        dependencyLookup[key]?.[normalizeComparableValue(parentValue)] || new Set();
      if (!allowedValues.size || !allowedValues.has(normalizeComparableValue(childValue))) {
        rowErrors.push(`${getColumnLabel(key)} does not match ${getColumnLabel(parentKey)} mapping.`);
      }
    });

    const rawAdditionalTags = String(row?.freeTextTags || "").trim();
    if (rawAdditionalTags) {
      const parsedTags = rawAdditionalTags.split(",").map((tag) => tag.trim());
      if (parsedTags.some((tag) => !tag)) {
        rowErrors.push(
          "Additional Tags must be comma-separated alphabetic tags (spaces allowed) without empty entries."
        );
      }
      if (parsedTags.length > 15) {
        rowErrors.push("Additional Tags can contain at most 15 tags.");
      }
      if (parsedTags.some((tag) => !/^[A-Za-z ]+$/.test(tag))) {
        rowErrors.push("Additional Tags must contain alphabets and spaces only.");
      }
      const normalizedTags = parsedTags.map((tag) => tag.toLowerCase());
      if (new Set(normalizedTags).size !== normalizedTags.length) {
        rowErrors.push("Additional Tags must not contain duplicate values.");
      }
    }

    if (rowErrors.length > 0) {
      failedRows.push({
        rowNumber,
        engagementId,
        errors: rowErrors,
        warnings: rowWarnings,
        row,
      });
      return;
    }

    validRows.push({
      rowNumber,
      engagementId,
      warnings: rowWarnings,
      row,
      existingRow,
    });
  });

  return {
    missingColumns: [],
    summary: {
      totalRecords: rows.length,
      validRecords: validRows.length,
      warningRecords: validRows.filter((item) => (item.warnings || []).length > 0).length,
      errorRecords: failedRows.length,
    },
    validRows,
    failedRows,
  };
};

export const buildXlsxBuffer = async ({
  records = [],
  userData = {},
  columns = EDITABLE_ENGAGEMENT_EXPORT_COLUMNS,
}) => {
  const dropdownColumns = columns.filter(
    (column) => column.type === "dropdown"
  );
  const dropdownSources = buildDropdownSources(userData);
  const dropdownWorkbookData = buildDropdownWorkbookData(dropdownSources);
  const zip = new JSZip();

  zip.file("[Content_Types].xml", buildContentTypesXml());
  zip.folder("_rels").file(".rels", buildRootRelsXml());
  zip.folder("xl").file("workbook.xml", buildWorkbookXml(dropdownWorkbookData.namedRanges));
  zip.folder("xl").folder("_rels").file("workbook.xml.rels", buildWorkbookRelsXml());
  zip.folder("xl").file("styles.xml", buildStylesXml());
  zip
    .folder("xl")
    .folder("worksheets")
    .file(
      "sheet1.xml",
      buildMainSheetXml(records, dropdownColumns, dropdownWorkbookData.validationConfig, columns)
    );
  zip
    .folder("xl")
    .folder("worksheets")
    .file("sheet2.xml", buildDropdownSheetXml(dropdownWorkbookData.sheetColumns));

  return zip.generateAsync({ type: "uint8array" });
};

export const createEditableEngagementExportFile = async ({
  engagements = [],
  userData = {},
  includeSource = false,
}) => {
  const columns = getEditableEngagementExportColumns({ includeSource });
  const records = buildEditableEngagementExportRows(engagements, userData, columns);
  const fileBase = `engagement_updates_${getTimestamp()}`;
  try {
    const xlsxBuffer = await buildXlsxBuffer({
      records,
      userData,
      columns,
    });
    return {
      fileName: `${fileBase}.xlsx`,
      blob: new Blob([xlsxBuffer], { type: XLSX_CONTENT_TYPE }),
      mimeType: XLSX_CONTENT_TYPE,
      recordsCount: records.length,
      format: "xlsx",
    };
  } catch (error) {
    const csvContent = buildCsvContent(records, columns);
    return {
      fileName: `${fileBase}.csv`,
      blob: new Blob([csvContent], { type: "text/csv;charset=utf-8;" }),
      mimeType: "text/csv;charset=utf-8;",
      recordsCount: records.length,
      format: "csv",
      fallbackReason: error?.message || "xlsx_generation_failed",
    };
  }
};

export const downloadBlobFile = ({ blob, fileName }) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
